import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useNavigate } from "react-router-dom";
import { toast_position } from "../../../../redux/features/Settings/Authorization";
import TEXT from "../../../../utils/globalText";
import SmallPopupWithInput from "../../CommonSmallPopupWithInput";
import { internalTxnDenied } from "../../../../redux/features/internalTransaction/internalTransactionDetailPage.js";
import UniversalRefreshToast from "../../UniversalRefreshToast/index.jsx";
import CommonToast from "../../CommonToast/index.jsx";
import {
  errorcode,
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider.js";
import ICONS from "../../../../assets/Icons/index.js";

const InternalTxnDeclinePopup = ({
  handleClose,
  txnId,
  allTxnData,
  updateAllTxn,
  addLifeCycleStatus,
  flow = "",
  onRejectSuccess,
}) => {
  const services = useSelector((state) => state.refreshToken.services);
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  const internalTxnRejectLoading = useSelector(
    (state) => state.InternalTxnSingleApproval,
  );

  const [declineReason, setDeclineReason] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);

  const handleInputChange = (event) => {
    let value = event.target.value;

    // 1. Allow only alphanumeric + space
    value = value.replace(/[^a-zA-Z0-9\s]/g, "");

    // 2. Remove leading spaces
    value = value.replace(/^\s+/, "");

    // 3. Allow only one space between words
    value = value.replace(/\s{2,}/g, " ");

    // 4. Limit to 150 characters
    value = value.slice(0, 150);

    setDeclineReason(value);
  };

  const handleTxnMultiRejecting = () => {
    setTimeout(() => {
      toast.dismiss();
    }, 2000);
  };

  const finalize = () => {
    setShowLoader(false);
    setInputDisabled(false);
    setDeclineReason("");
    handleClose();
  };

  const handleReject = async (id, reason) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      if (
        services?.find(
          (service) => service === "AInternalTxn" || service === "Admin",
        ) !== undefined
      ) {
        // Token Check
        if (!sessionStorage.getItem("q2p_token")) {
          return;
        }

        setShowLoader(true);
        setInputDisabled(true);

        UniversalRefreshToast("Rejecting transaction");
        handleTxnMultiRejecting();

        // Call correct API
        const apiCall = internalTxnDenied({ id, AccountIdData: reason, flow });

        const result = await dispatch(apiCall);
        const payload = result?.payload;

        // wait for loader animation
        await new Promise((resolve) =>
          setTimeout(resolve, IntervalConfiguration.intervalExtraLarge),
        );

        if (payload?.status === errorcode.errorFailed) {
          CommonToast({
            text: payload?.error,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
          });
          return finalize();
        }

        if (
          payload?.errors?.user_unauthorized?.[
            GlobalConfiguration.lengthCheckZero
          ]
        ) {
          Navigate("/feature_not_assigned", {
            state: { unavailableService: "AInternalTxn, Admin" },
          });
          return finalize();
        }

        // MULTI approval flow
        if (flow === "multiApproval") {
          if (payload?.status === false) {
            Navigate(
              "/internal_transaction_dashboard/internal_transaction_multi_approval/multi_approval/error",
            );
          } else {
            Navigate(
              "/internal_transaction_dashboard/internal_transaction_multi_approval/multi_approval/declined",
            );
          }
          return finalize();
        }

        // SINGLE approval flow
        let hasShownErrorToast = false;
        const updatedData = allTxnData.map((item) => {
          if (item.txn_id !== id) return item;

          const tx =
            payload?.transactions?.[GlobalConfiguration.lengthCheckZero];
          let lifecycleStatus = "error";
          let errorMessage = "";

          // lifecycle status logic
          if (payload?.status === true) {
            lifecycleStatus =
              tx?.lifecycle_status === "rejected"
                ? "rejected"
                : tx?.lifecycle_status === "pending"
                  ? "pendingOne"
                  : "error";
          }

          // error message handling
          if (
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]?.errors
          ) {
            const errors =
              payload.errors.base[GlobalConfiguration.lengthCheckZero].errors;
            errorMessage = errors[GlobalConfiguration.lengthCheckZero];

            errors.forEach((msg) => {
              if (payload?.status === false) {
                hasShownErrorToast = true;
                CommonToast({
                  text: msg,
                  icon: ICONS?.ERROR_WHITE_CIRCLE,
                  variant: "var(--error)",
                });
              }
            });
          } else if (
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]
          ) {
            errorMessage =
              payload.errors.base[GlobalConfiguration.lengthCheckZero];
          }

          const finalLifecycleStatus =
            payload?.status === true ? "rejected" : item.lifecycle_status;

          const uiState = addLifeCycleStatus({
            lifecycle_status: finalLifecycleStatus,
            distribution_lifecycle_status: item.distribution_lifecycle_status,
            services,
          });

          return {
            ...item,
            lifecycle_status: finalLifecycleStatus,
            rejected: true,
            rejection_reason: reason,
            error: payload?.status === false ? "true" : "false",
            message: payload?.status === false ? errorMessage : "",
            ...uiState,
          };
        });

        updateAllTxn(updatedData);

        // toast results
        if (payload?.status === false && !hasShownErrorToast) {
          const err =
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]
              ?.errors?.[GlobalConfiguration.lengthCheckZero] ||
            payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero] ||
            "Error occurred";
          CommonToast({
            text: err,
            icon: ICONS?.ERROR_WHITE_CIRCLE,
            variant: "var(--error)",
          });
        } else if (payload?.status === true) {
          CommonToast({
            text: "Transaction rejected successfully.",
            icon: "https://q2pay.in/new_assets/common/approval/select_all_with_white_filled_circle.svg",
            variant: "var(--success)",
          });

          let storedData = JSON.parse(sessionStorage.getItem("selectedData"));

          if (storedData) {
            const updatedTransactions =
              storedData.selectedSendTransactions?.filter(
                (txn) => txn.txn_id !== id,
              );

            const updatedTransactionIds =
              storedData.selectedTransactions?.filter((txnId) => txnId !== id);

            sessionStorage.setItem(
              "selectedData",
              JSON.stringify({
                ...storedData,
                selectedSendTransactions: updatedTransactions,
                selectedTransactions: updatedTransactionIds,
              }),
            );
          }

          setTimeout(() => {
            onRejectSuccess?.();
            finalize();
          }, 3000);
        }

        finalize();
      } else {
        Navigate("/feature_not_assigned", {
          state: {
            unavailableService: "AInternalTxn, Admin",
          },
        });
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const emojiRegex =
        /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
      if (emojiRegex.test(event.key)) {
        event.preventDefault();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();

        if (!inputDisabled && declineReason.trim().length >= 10) {
          handleReject(txnId, declineReason);
        }
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputDisabled, declineReason, txnId]);

  const [isFocused, setIsFocused] = useState(false);

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }

    return focused ? "typing" : "error";
  };

  return (
    <div className="fixed top-top-base left-left-base w-full-width h-full-height z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
      <SmallPopupWithInput
        headerTitle={TEXT.REJECT_REASON_LABEL}
        headerFontWeight="semibold"
        headerFontColor="text-primary"
        textSize="font-md"
        secondHeaderTitle={TEXT.REJECTION_REASON_PROMPT}
        // secondHeaderFontWeight="semibold"
        secondHeaderFontColor="text-secondary"
        secondHeaderTextSize="font-xs"
        inputPlaceholder={TEXT.REASON_10_150_CHAR}
        inputOnFocus={(e) => setIsFocused(true)}
        inputOnBlur={(e) => setIsFocused(false)}
        inputVariant={computeVariant(declineReason, isFocused)}
        inputBgColor="var(--primary-bg)"
        inputFgColor="var(--text-secondary)"
        inputOnInput={(e) => {
          if (!showLoader) {
            handleInputChange(e);
          }
        }}
        inputValue={declineReason}
        inputFontKind="small"
        inputIsFullWidth={true}
        // End of Input Field Props
        btnOneText={TEXT.CANCEL}
        btnOneHandleFunction={() => {
          if (!showLoader) {
            handleClose();
          }
        }}
        btnOneEnabledBgColor="var(--primary-bg)"
        btnOneOutLineColor="var(--text-primary)"
        btnOneEnabledTextColor="var(--text-primary)"
        btnOneHoverOption="white"
        btnOneIsDisabled={showLoader}
        btnTwoText={TEXT.REJECT}
        btnTwoHandleFunction={() => {
          if (!declineReason || declineReason.length < 10 || showLoader) {
            return;
          }
          handleReject(txnId, declineReason);
        }}
        btnTwoEnabledBgColor="var(--negative-act)"
        btnTwoEnabledTextColor="var(--text-primary)"
        btnTwoIsDisabled={!declineReason || declineReason.length < 10}
        btnTwoDisabledBgColor="var(--text-disabled)"
        btnTwoIsLoaderNeeded={showLoader ? true : false}
        btnTwoIsLoading={showLoader ? true : false}
        btnTwoHoverOption="black"
        btnTwoInActiveVariantNeeded={true}
      />
    </div>
  );
};

export default InternalTxnDeclinePopup;
